import { useState, useRef, useEffect } from 'react';
import type { IconButtonProps } from '@mui/material/IconButton';

import { tss } from 'tss-react/mui';

import { Tooltip, IconButton, CircularProgress } from '@mui/material';

import { ConfirmDialog } from '../feedback/ConfirmDialog';

import type { Or2ButtonProps } from './Or2Button';
import { Or2Button } from './Or2Button';

const useStyles = tss.create(({ theme }) => {
  return {
    buttonLoading: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      marginTop: theme.spacing(-1),
      marginLeft: theme.spacing(-1.5),
    },
    buttonLoadingIcon: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      marginTop: theme.spacing(-1),
      marginLeft: theme.spacing(-1),
    },
    wrapper: {
      position: 'relative',
    },
    root: {
      display: 'inline-flex',
      alignItems: 'center',
    },
  };
});

export interface LoadableConfirmationButtonProps extends Or2ButtonProps {
  titleText?: React.ReactNode;
  ariaLabel?: string | undefined;
  loading?: boolean;
  confirmableText?: React.ReactNode | undefined;
  okText?: string | undefined;
  cancelText?: string | undefined;
  children: React.ReactNode;
  icon?: boolean | undefined;
  className?: string | undefined;
  tooltipTitle?: React.ReactNode | undefined;
  confirmButtonProps?: Or2ButtonProps | undefined;
  onClick?: () => unknown | Promise<unknown>;
}

export function LoadableConfirmationButton({
  titleText,
  ariaLabel,
  loading = false,
  confirmableText,
  okText = 'Ok',
  cancelText = 'Cancel',
  children,
  icon,
  className,
  tooltipTitle,
  confirmButtonProps,
  onClick,
  disabled,
  size,
  ...otherProps
}: LoadableConfirmationButtonProps) {
  const { classes, cx } = useStyles();
  const stillHereRef = useRef(true);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    stillHereRef.current = true;

    return () => {
      stillHereRef.current = false;
    };
  }, []);

  function handleClick() {
    if (confirmableText) {
      setDialogOpen(true);
    } else if (onClick) {
      onClick();
    }
  }

  function triggerClose() {
    if (stillHereRef.current) {
      setDialogOpen(false);
    }
  }

  if (!tooltipTitle) {
    tooltipTitle = '';
  }

  return (
    <>
      <Tooltip title={tooltipTitle}>
        <span className={cx(classes.root, className)}>
          <span className={classes.wrapper}>
            <>
              {icon ? (
                <>
                  <IconButton
                    {...(otherProps as IconButtonProps)} /* this is bad */
                    size={size ?? 'small'}
                    disabled={loading || disabled}
                    onClick={handleClick}
                  >
                    {children}
                  </IconButton>
                  {/* only show progress on button if confirm dialog doesnt appear, dialog will show instead */}
                  {loading && !confirmableText && (
                    <CircularProgress
                      size={16}
                      className={classes.buttonLoadingIcon}
                    />
                  )}
                </>
              ) : (
                <>
                  <Or2Button
                    size={size}
                    disabled={loading || disabled}
                    onClick={handleClick}
                    {...otherProps}
                  >
                    {children}
                  </Or2Button>
                  {/* only show progress on button if confirm dialog doesnt appear, dialog will show instead */}
                  {loading && !confirmableText && (
                    <CircularProgress
                      size={16}
                      className={classes.buttonLoading}
                    />
                  )}
                </>
              )}
            </>
          </span>
        </span>
      </Tooltip>
      {confirmableText && onClick ? (
        <ConfirmDialog
          titleText={titleText}
          ariaLabel={ariaLabel}
          open={dialogOpen}
          innerText={confirmableText}
          confirmText={okText}
          cancelText={cancelText}
          confirmButtonProps={confirmButtonProps}
          loading={loading}
          onConfirm={async () => {
            await onClick();
            triggerClose();
          }}
          onClose={triggerClose}
        />
      ) : null}
    </>
  );
}
